import { useState, useEffect } from 'react';
import api from '../../services/api';
import { Clock, CheckCircle } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useTenant } from '../../context/TenantContext';
import { formatIST } from '../../utils/dateUtils';
import Loading from '../../components/Loading';

const LiveTimer = ({ startTime }) => {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
        const updateTimer = () => {
            let finalStartTime = startTime;
            if (typeof startTime === 'string' && !startTime.includes('Z') && !startTime.includes('+')) {
                finalStartTime = startTime.includes(' ') ? startTime.replace(' ', 'T') + 'Z' : startTime + 'Z';
            }
            const start = new Date(finalStartTime).getTime();
            const now = Date.now();
            const diff = now - start;
            if (diff < 0) return setElapsed('00:00');
            
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            
            if (hours > 0) {
                setElapsed(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
            } else {
                setElapsed(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [startTime]);

    return (
        <span className="font-mono text-sm inline-flex items-center text-red-600 font-medium bg-red-50 px-2 py-0.5 rounded">
            <Clock className="w-3 h-3 mr-1" /> {elapsed}
        </span>
    );
};

export default function KitchenDashboard() {
    const toast = useToast();
    const { tenant } = useTenant();
    const [orders, setOrders] = useState([]);
    const [historyOrders, setHistoryOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('active'); // 'active', 'work', 'stock'
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [workSearchOrder, setWorkSearchOrder] = useState('');
    const [workSearchDate, setWorkSearchDate] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [expandedOrderId, setExpandedOrderId] = useState(null);

    useEffect(() => {
        fetchOrders();
        const interval = setInterval(fetchOrders, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, []);

    const fetchOrders = async () => {
        try {
            const [activeRes, historyRes] = await Promise.all([
                api.get('/api/orders/kitchen/active'),
                api.get('/api/orders/kitchen/my-work')
            ]);
            setOrders(activeRes.data);
            setHistoryOrders(historyRes.data);
        } catch (error) {
            console.error('Failed to fetch kitchen orders', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (orderId, newStatus) => {
        setActionLoading(orderId);
        try {
            await api.patch(`/api/orders/${orderId}/kitchen-status`, { status: newStatus });
            await fetchOrders();
        } catch (error) {
            toast.error('Failed to update status');
        } finally {
            setActionLoading(null);
        }
    };

    const fetchMenuItems = async () => {
        setStockLoading(true);
        try {
            const response = await api.get('/api/menu/items');
            setMenuItems(response.data);
        } catch (error) {
            console.error('Failed to fetch menu items', error);
        } finally {
            setStockLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'stock') {
            fetchMenuItems();
        }
    }, [activeTab]);

    const handleStockUpdate = async (id, newStock) => {
        if (newStock < 0) return;
        try {
            await api.patch(`/api/menu/items/${id}/stock`, { stockQuantity: newStock });
            // Update local state without re-fetching everything
            setMenuItems(items => items.map(item => item.id === id ? { ...item, stock_quantity: newStock } : item));
        } catch (error) {
            toast.error('Failed to update stock');
        }
    };

    const filteredMenuItems = menuItems.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory ? item.category_name === selectedCategory : true;
        return matchesSearch && matchesCategory;
    });

    const uniqueCategories = [...new Set(menuItems.map(item => item.category_name).filter(Boolean))];

    const filteredHistoryOrders = historyOrders.filter(order => {
        const matchesOrder = String(order.id).toLowerCase().includes(workSearchOrder.toLowerCase());
        const matchesDate = workSearchDate ? new Date(order.updated_at).toLocaleDateString('en-CA') === workSearchDate : true;
        return matchesOrder && matchesDate;
    });

    if (loading) return <Loading mode="page" message="Accessing kitchen orders..." />;

    const primaryTabStyle = (tabName) => activeTab === tabName && tenant?.primary_color ? { borderBottomColor: tenant.primary_color, color: tenant.primary_color } : {};
    const primaryBorderStyle = tenant?.primary_color ? { borderTopColor: tenant.primary_color } : {};

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            {actionLoading && (
                <Loading mode="full" message="Updating kitchen..." />
            )}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-6">
                <h1 className="text-2xl font-bold text-gray-900 text-center sm:text-left">Kitchen Display System (KDS)</h1>
                <div className="bg-primary-100 text-primary-800 px-4 py-2 rounded-lg font-medium text-center" style={tenant?.primary_color ? { backgroundColor: `${tenant.primary_color}20`, color: tenant.primary_color } : {}}>
                    {orders.length} Active Orders
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b mb-6 overflow-x-auto hide-scrollbar whitespace-nowrap">
                <button
                    onClick={() => setActiveTab('active')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors ${activeTab === 'active'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    style={primaryTabStyle('active')}
                >
                    Active Orders
                </button>
                <button
                    onClick={() => setActiveTab('work')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors ${activeTab === 'work'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    style={primaryTabStyle('work')}
                >
                    My Work ({historyOrders.length})
                </button>
                <button
                    onClick={() => setActiveTab('stock')}
                    className={`px-6 py-3 font-semibold text-sm transition-colors ${activeTab === 'stock'
                        ? 'border-b-2 border-primary-600 text-primary-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    style={primaryTabStyle('stock')}
                >
                    Manage Stock
                </button>
            </div>

            <div className={`gap-6 ${activeTab === 'stock' ? 'block' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                {activeTab === 'active' && (
                    orders.length === 0 ? (
                        <div className="col-span-full py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm">
                            No active orders to prepare at the moment.
                        </div>
                    ) : (
                        orders.map(order => (
                            <div key={order.id} className="card p-6 flex flex-col h-full border-t-4 border-primary-500" style={primaryBorderStyle}>
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <span className="text-xl font-bold">#{String(order.id).slice(0, 5)}</span>
                                        <p className="text-sm text-gray-500">
                                            {formatIST(order.updated_at || order.created_at)}
                                        </p>
                                        <div className="mt-1">
                                            <LiveTimer startTime={order.updated_at || order.created_at} />
                                        </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${order.status === 'preparing' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {order.status}
                                    </span>
                                </div>

                                <div className="flex-1">
                                    <ul className="space-y-3 mb-6 divide-y divide-gray-100">
                                        {(order.items || []).map(item => (
                                            <li key={item.id} className="pt-3">
                                                <div className="flex justify-between">
                                                    <span className="font-medium text-gray-900">{item.quantity}x {item.name}</span>
                                                </div>
                                                {item.special_instructions && (
                                                    <p className="text-sm text-red-600 italic mt-1 bg-red-50 p-1 rounded">
                                                        Note: {item.special_instructions}
                                                    </p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mt-auto space-y-2">
                                    {order.status === 'confirmed' && (
                                        <button
                                            onClick={() => updateStatus(order.id, 'preparing')}
                                            disabled={actionLoading === order.id}
                                            className="w-full btn-primary bg-yellow-500 flex items-center justify-center hover:bg-yellow-600 disabled:opacity-50"
                                        >
                                            <Clock className="w-5 h-5 mr-2" />
                                            {actionLoading === order.id ? 'Updating...' : 'Start Preparing'}
                                        </button>
                                    )}
                                    {order.status === 'preparing' && (
                                        <button
                                            onClick={() => updateStatus(order.id, 'ready')}
                                            disabled={actionLoading === order.id}
                                            className="w-full btn-primary bg-green-600 flex items-center justify-center hover:bg-green-700 disabled:opacity-50"
                                        >
                                            <CheckCircle className="w-5 h-5 mr-2" />
                                            {actionLoading === order.id ? 'Updating...' : 'Mark as Ready'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )
                )}

                {activeTab === 'work' && (
                    <div className="col-span-full mb-6 flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
                            <input
                                type="text"
                                placeholder="Search order number..."
                                value={workSearchOrder}
                                onChange={(e) => setWorkSearchOrder(e.target.value)}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Date</label>
                            <input
                                type="date"
                                value={workSearchDate}
                                onChange={(e) => setWorkSearchDate(e.target.value)}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'work' && (
                    /* My Work Tab */
                    filteredHistoryOrders.length === 0 ? (
                        <div className="col-span-full py-12 text-center text-gray-500 bg-white rounded-xl shadow-sm">
                            No orders matching your search.
                        </div>
                    ) : (
                        filteredHistoryOrders.map(order => (
                            <div key={order.id} 
                                className={`card p-6 flex flex-col h-full border-t-4 cursor-pointer transition-all ${expandedOrderId === order.id ? 'border-primary-500 shadow-lg' : 'border-gray-300 opacity-80 hover:opacity-100 hover:shadow-md'}`}
                                onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <span className="text-xl font-bold text-gray-700">#{String(order.id).slice(0, 5)}</span>
                                        <p className="text-sm text-gray-500">
                                            {new Date(order.updated_at).toLocaleDateString()} at {new Date(order.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                        {order.status === 'preparing' && (
                                            <div className="mt-1">
                                                <LiveTimer startTime={order.updated_at} />
                                            </div>
                                        )}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                                        order.status === 'preparing' ? 'bg-yellow-100 text-yellow-800' :
                                        order.status === 'ready' ? 'bg-green-100 text-green-800' :
                                        order.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                        {order.status}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <ul className="space-y-3 mb-2 divide-y divide-gray-100">
                                        {(order.items || []).map(item => (
                                            <li key={item.id} className="pt-3">
                                                <div className="text-gray-600 font-medium">{item.quantity}x {item.name}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Expanded Details */}
                                {expandedOrderId === order.id && (
                                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3" onClick={(e) => e.stopPropagation()}>
                                        <h4 className="font-bold text-gray-800">Order Details</h4>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div><span className="text-gray-500">Order ID:</span></div>
                                            <div className="font-mono text-xs">{order.id}</div>
                                            <div><span className="text-gray-500">Customer:</span></div>
                                            <div>{order.customer_name || '-'}</div>
                                            <div><span className="text-gray-500">Phone:</span></div>
                                            <div>{order.contact_phone || '-'}</div>
                                            {order.delivery_address && (<>
                                                <div><span className="text-gray-500">Delivery:</span></div>
                                                <div>{order.delivery_address}</div>
                                            </>)}
                                            <div><span className="text-gray-500">Total:</span></div>
                                            <div className="font-bold">₹{parseFloat(order.final_total_amount || order.total_amount || 0).toFixed(2)}</div>
                                            <div><span className="text-gray-500">Payment:</span></div>
                                            <div>{order.payment_status === 'paid' ? '✅ Paid' : '⏳ Pending'}</div>
                                        </div>
                                        {/* Item-level details */}
                                        <div className="mt-3">
                                            <h5 className="font-semibold text-gray-700 text-sm mb-2">Items Breakdown</h5>
                                            <div className="space-y-1">
                                                {(order.items || []).map(item => (
                                                    <div key={item.id} className="flex justify-between text-sm bg-gray-50 px-3 py-1.5 rounded">
                                                        <span>{item.quantity}x {item.name}</span>
                                                        <span className="font-medium">₹{((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Delivery info for delivered orders */}
                                        {order.status === 'delivered' && (
                                            <div className="mt-3 bg-green-50 p-3 rounded-lg">
                                                <h5 className="font-semibold text-green-800 text-sm mb-1">✅ Delivery Completed</h5>
                                                <div className="text-sm text-green-700 space-y-1">
                                                    {order.delivery_partner_name && <p>Partner: {order.delivery_partner_name}</p>}
                                                    {order.delivered_at && <p>Delivered at: {new Date(order.delivered_at).toLocaleString()}</p>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )
                )}

                {activeTab === 'stock' && (
                    <div className="col-span-full">
                        <div className="mb-4 flex flex-col sm:flex-row gap-4">
                            <input
                                type="text"
                                placeholder="Search menu items..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full sm:w-1/2 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="w-full sm:w-1/2 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                            >
                                <option value="">All Categories</option>
                                {uniqueCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                        {stockLoading ? (
                            <Loading mode="page" message="Inventory check in progress..." />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {filteredMenuItems.map(item => (
                                    <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border flex flex-col justify-between h-full">
                                        <div className="mb-4">
                                            <h3 className="font-bold text-gray-900 leading-tight">{item.name}</h3>
                                            <p className="text-xs text-gray-500">{item.category_name}</p>
                                        </div>
                                        <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                                            <button
                                                onClick={() => handleStockUpdate(item.id, Number(item.stock_quantity) - 1)}
                                                disabled={item.stock_quantity <= 0}
                                                className="w-8 h-8 rounded bg-white shadow-sm border flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                            >
                                                -
                                            </button>
                                            <div className="text-center font-bold text-lg px-2 min-w-[3rem]">
                                                {item.stock_quantity}
                                            </div>
                                            <button
                                                onClick={() => handleStockUpdate(item.id, Number(item.stock_quantity) + 1)}
                                                className="w-8 h-8 rounded bg-white shadow-sm border flex items-center justify-center text-gray-600 hover:bg-gray-100"
                                            >
                                                +
                                            </button>
                                        </div>
                                        {item.stock_quantity <= 5 && (
                                            <div className="text-xs text-center mt-2 font-semibold text-red-600">
                                                {item.stock_quantity <= 0 ? 'Out of Stock' : 'Low Stock'}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {filteredMenuItems.length === 0 && (
                                    <div className="col-span-full py-8 text-center text-gray-500">No items match your search.</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
